import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import SectionEyebrow from "@/components/site/SectionEyebrow";
import BrandPhoto from "@/components/site/BrandPhoto";
import ConsultationBand from "@/components/site/ConsultationBand";
import {
  ABOUT_BIO_PARAGRAPHS,
  ROLES,
  RECOGNITION,
  BRAND,
} from "@/lib/content";

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About"
        title="The work is strategy. The foundation is people."
        lead="Wanakee Trask is an educator, consultant, and keynote speaker with more than a decade of experience across education, youth development, school and community partnerships, and stakeholder engagement."
        testId="about-page-header"
      />

      <section className="pb-8 md:pb-12">
        <div className="wt-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <BrandPhoto
                src="/images/wanakee-portrait.jpeg"
                alt="Portrait of Wanakee Trask"
                ratio="portrait"
                testId="about-portrait"
              />
            </div>
            <div className="lg:col-span-7 space-y-6">
              {ABOUT_BIO_PARAGRAPHS.map((p, i) => (
                <p key={i} className="wt-body" data-testid={`about-bio-${i}`}>
                  {p}
                </p>
              ))}
              <div className="wt-hairline" />
              <div>
                <SectionEyebrow>Roles &amp; premise</SectionEyebrow>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {ROLES.map((role, i) => {
                    const isEG = role.startsWith("Founder and Executive Director, Elevate Greatness");
                    return (
                      <li
                        key={role}
                        data-testid={`about-role-${i}`}
                        className="flex gap-3 items-start text-sm"
                      >
                        <span
                          className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: "var(--wt-gold)" }}
                          aria-hidden="true"
                        />
                        {isEG ? (
                          <span>
                            Founder and Executive Director,{" "}
                            <a
                              href={BRAND.elevateGreatnessUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="about-elevate-greatness-link"
                              className="wt-link inline-flex items-center gap-1"
                            >
                              Elevate Greatness
                              <ExternalLink size={12} aria-hidden="true" />
                            </a>
                          </span>
                        ) : (
                          <span>{role}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="wt-section"
        style={{ backgroundColor: "#FBF8F1" }}
        data-testid="about-approach"
      >
        <div className="wt-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionEyebrow>Approach</SectionEyebrow>
              <h2 className="wt-h2 mt-4">Durable systems, not one-time events.</h2>
            </div>
            <div className="lg:col-span-7 space-y-5">
              <p className="wt-body">
                Wanakee partners with district and school leaders to treat school climate as
                measurable Tier 1 infrastructure, align systems of belonging with attendance,
                behavior, and engagement data, and build the relational conditions that drive
                student engagement and educator morale.
              </p>
              <p className="wt-body">
                Her approach moves community engagement from episodic events toward accountable,
                sustainable networks of wraparound support. Initiatives move from compliance and
                documentation toward consistent, lived practice. Programs become repeatable and
                are no longer dependent on any single person.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="wt-section" data-testid="about-recognition">
        <div className="wt-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <SectionEyebrow>Recognition</SectionEyebrow>
              <h2 className="wt-h2 mt-4">Acknowledged across communities she serves.</h2>
              <ul className="mt-6 space-y-3">
                {RECOGNITION.map((r, i) => (
                  <li
                    key={r}
                    data-testid={`about-recognition-${i}`}
                    className="flex gap-3 items-start"
                  >
                    <span
                      className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: "var(--wt-gold)" }}
                      aria-hidden="true"
                    />
                    <span className="wt-body text-sm sm:text-base">{r}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link
                  to="/book-consultation"
                  data-testid="about-cta-book"
                  className="wt-btn-primary"
                >
                  Book a Consultation
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <BrandPhoto
                src="/images/wanakee-honoree.jpeg"
                alt="Wanakee Trask accepting an honoree award"
                ratio="portrait"
                caption="Honoree, 2026 Blacktopia Gala · Association Chicago"
                testId="about-honoree-photo"
              />
            </div>
          </div>
        </div>
      </section>

      <ConsultationBand />
    </div>
  );
}
